import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/useAuthStore'
import { useBusinessSettingsStore } from '@/stores/useBusinessSettingsStore'
import { tokenManager, isApiError, refreshAccessToken, setUnauthorizedHandler } from '@/api/client'
import { AuthUnreachable } from './AuthUnreachable'

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false)
  const fetchCurrentUser = useAuthStore((state) => state.fetchCurrentUser)
  const logout = useAuthStore((state) => state.logout)

  // A 401 that survives request()'s own single-flight refresh-and-retry means
  // the session is unrecoverable (no refresh token, or it's expired/revoked).
  // Register the handler here rather than in client.ts, which cannot import
  // useAuthStore directly (useAuthStore.ts already imports from client.ts —
  // a static import back would create a cycle).
  useEffect(() => {
    setUnauthorizedHandler(() => logout())
    return () => setUnauthorizedHandler(null)
  }, [logout])

  // Consecutive transient failures (network error, timeout, 5xx) of a stored
  // token's check. With a persisted user the app renders and later calls
  // retry; without one, ProtectedRoute would send a still-signed-in owner to
  // /login, so the check is retried here behind an "unreachable" screen.
  const [failures, setFailures] = useState(0)

  const verifySession = useCallback(async () => {
    try {
      await fetchCurrentUser()
      void useBusinessSettingsStore.getState().fetchSettings()
    } catch (error) {
      // Only a confirmed 401 means the access token is genuinely invalid —
      // verify with a refresh attempt before logging out.
      if (isApiError(error) && error.status === 401) {
        const refreshedToken = await refreshAccessToken()
        if (!refreshedToken) {
          logout()
        } else {
          try {
            await fetchCurrentUser()
            void useBusinessSettingsStore.getState().fetchSettings()
          } catch {
            // Still failing right after a successful refresh — leave the
            // session as-is; subsequent API calls will surface the error.
          }
        }
      } else if (!useAuthStore.getState().isAuthenticated) {
        setFailures((n) => n + 1)
        return
      }
    }
    setFailures(0)
    setIsInitialized(true)
  }, [fetchCurrentUser, logout])

  useEffect(() => {
    async function initializeAuth() {
      const token = tokenManager.getAccessToken()
      // Dev mock token: seed a fallback user first so a failed /auth/me can
      // never wipe the dev session, then TRY the real /auth/me — it carries
      // `is_admin`, without which AdminRoute 404s the /admin dashboard even
      // when the DB flag is set. Offline (backend down / wrong backend on
      // :8000) the seeded user stays and the error is swallowed — no logout.
      if (import.meta.env.DEV && token === 'mock-access-token-dev') {
        if (!useAuthStore.getState().isAuthenticated) {
          useAuthStore.getState().setUser({
            id: 'dev',
            email: 'dev@maxflow-dev.com',
            default_business_id: '6330cff3-915b-4ef6-bf14-25a9485ca470',
          } as never)
        }
        try {
          await fetchCurrentUser()
        } catch {
          // Backend unreachable — keep the seeded dev user.
        }
        // Load business settings so the business timezone is available app-wide
        // (scheduled-time display, first-schedule prompt) without each surface
        // fetching it.
        void useBusinessSettingsStore.getState().fetchSettings()
        setIsInitialized(true)
        return
      }
      if (token) {
        await verifySession()
        return
      }
      setIsInitialized(true)
    }

    initializeAuth()
  }, [fetchCurrentUser, verifySession])

  // While unreachable: retry with backoff, and at once when the browser
  // reports it is back online.
  useEffect(() => {
    if (failures === 0) return
    const retry = () => void verifySession()
    const timer = window.setTimeout(retry, Math.min(1000 * 2 ** failures, 30_000))
    window.addEventListener('online', retry)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('online', retry)
    }
  }, [failures, verifySession])

  if (failures > 0) {
    return <AuthUnreachable onRetry={() => void verifySession()} />
  }

  if (!isInitialized) {
    return (
      <div
        data-testid="auth-checking"
        className="bg-background flex h-screen w-screen flex-col items-center justify-center gap-6"
      >
        <img src="/assets/logo-static-dark.svg?v=3" alt="MaxFlow" className="h-12 w-auto" />
        <div className="flex flex-col items-center gap-3">
          <div className="bg-muted h-1 w-48 overflow-hidden rounded-full">
            <div className="bg-primary/40 h-full w-1/3 animate-[shimmer_1.5s_ease-in-out_infinite] rounded-full" />
          </div>
          <p className="text-muted-foreground text-sm">Loading your workspace...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
